import React, { useState } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { format } from 'date-fns';
import { 
  Calendar, Plus, Loader2, Trash2, Pencil, Check, ArrowRight,
  AlertTriangle, GitBranch, Clock, GripVertical, ArrowUpDown
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { PHASE_CONFIG } from '../hooks/useTimelineManagement';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Helper to parse date strings as local dates
const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr.length === 10 && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return new Date(dateStr + 'T12:00:00');
  }
  return new Date(dateStr);
};

// Get timeline position as percentage
const getTimelinePosition = (dateStr, projectStart, mvpDeadline) => {
  if (!projectStart || !mvpDeadline) return 50;
  const start = new Date(projectStart).getTime();
  const end = new Date(mvpDeadline).getTime();
  const date = new Date(dateStr).getTime();
  const position = ((date - start) / (end - start)) * 100;
  return Math.max(0, Math.min(100, position));
};

// Get milestone color based on status
const getMilestoneColor = (status) => {
  switch (status) {
    case 'completed': return 'bg-green-500';
    case 'in_progress': return 'bg-blue-500';
    case 'delayed': return 'bg-red-500';
    default: return 'bg-slate-400';
  }
};

// Sortable Milestone Row Component
const SortableMilestoneRow = ({ milestone, canEdit, onEdit, onDelete, onUpdate }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: milestone.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const phaseConfig = PHASE_CONFIG[milestone.phase_type] || PHASE_CONFIG.development;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg ${isDragging ? 'ring-2 ring-blue-500' : ''}`}
    >
      <div className="flex items-center gap-3">
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded"
        >
          <GripVertical className="h-4 w-4 text-slate-400" />
        </button>
        <span className="text-sm">{phaseConfig.icon}</span>
        <div className={`w-3 h-3 rounded-full ${getMilestoneColor(milestone.status)}`} />
        <div>
          <p className="font-medium text-sm">{milestone.name}</p>
          <p className="text-xs text-muted-foreground">
            {milestone.start_date && `${format(parseLocalDate(milestone.start_date), 'MMM d')} → `}
            {format(parseLocalDate(milestone.target_date), 'MMM d, yyyy')}
            {milestone.hub && ` • ${milestone.hub}`}
          </p>
        </div>
      </div>
      {canEdit && (
        <div className="flex items-center gap-2">
          <Select
            value={milestone.status}
            onValueChange={(value) => onUpdate(milestone.id, { status: value })}
          >
            <SelectTrigger className="h-7 w-24 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="delayed">Delayed</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={() => onEdit(milestone)} className="h-7 w-7 p-0 text-blue-500">
            <Pencil className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(milestone.id)} className="h-7 w-7 p-0 text-red-500">
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      )}
    </div>
  );
};

export const TimelineModal = ({
  open,
  onOpenChange,
  timelineData,
  timelineLoading,
  canEdit,
  // Date editing
  editingDates,
  setEditingDates,
  dateForm,
  setDateForm,
  onUpdateProjectDates,
  // Visualization toggles
  showDependencies,
  setShowDependencies,
  showCriticalPath,
  setShowCriticalPath,
  criticalPathIds,
  // Hover state
  hoveredMilestone,
  setHoveredMilestone,
  // Actions
  onAddMilestone,
  onAddTestPeriod,
  onEditMilestone,
  onEditTestPeriod,
  onUpdateMilestone,
  onDeleteMilestone,
  onDeleteTestPeriod,
  onSelectMilestone,
  onReorderMilestones
}) => {
  const [reorderMode, setReorderMode] = useState(false);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = timelineData.milestones.findIndex(m => m.id === active.id);
      const newIndex = timelineData.milestones.findIndex(m => m.id === over.id);
      
      const newOrder = arrayMove(timelineData.milestones, oldIndex, newIndex);
      const milestoneIds = newOrder.map(m => m.id);
      
      if (onReorderMilestones) {
        onReorderMilestones(milestoneIds);
      }
    }
  };

  // Helper for position calculation with current timeline data
  const getPosition = (dateStr) => {
    return getTimelinePosition(dateStr, timelineData.project_start, timelineData.mvp_deadline);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            RHINO-1 Project Timeline
          </DialogTitle>
          <DialogDescription>
            Visual timeline to MVP with feature milestones
          </DialogDescription>
        </DialogHeader>
        
        {timelineLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Timeline Header with Editable Dates */}
            <div className="flex items-center justify-between flex-wrap gap-4">
              {editingDates ? (
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground">Start:</Label>
                    <Input
                      type="date"
                      value={dateForm.project_start}
                      onChange={(e) => setDateForm({ ...dateForm, project_start: e.target.value })}
                      className="h-8 w-40"
                    />
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground">MVP:</Label>
                    <Input
                      type="date"
                      value={dateForm.mvp_deadline}
                      onChange={(e) => setDateForm({ ...dateForm, mvp_deadline: e.target.value })}
                      className="h-8 w-40"
                    />
                  </div>
                  <Button size="sm" onClick={onUpdateProjectDates}>
                    <Check className="h-4 w-4 mr-1" />
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingDates(false)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <div className="text-sm">
                    <span className="text-muted-foreground">Start:</span>{' '}
                    <span className="font-medium">{timelineData.project_start ? format(parseLocalDate(timelineData.project_start), 'MMM d, yyyy') : 'Not set'}</span>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  <div className="text-sm">
                    <span className="text-muted-foreground">MVP Target:</span>{' '}
                    <Badge className="bg-blue-600 text-white ml-1">
                      {timelineData.mvp_deadline ? format(parseLocalDate(timelineData.mvp_deadline), 'MMM d, yyyy') : 'Not set'}
                    </Badge>
                  </div>
                  {canEdit && (
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => {
                        setDateForm({
                          project_start: timelineData.project_start || '',
                          mvp_deadline: timelineData.mvp_deadline || ''
                        });
                        setEditingDates(true);
                      }}
                      className="h-7 px-2"
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="secondary">
                  {timelineData.completed_count}/{timelineData.total_count} Milestones
                </Badge>
                {/* Critical Path Toggle */}
                {criticalPathIds.length > 1 && (
                  <Button 
                    size="sm" 
                    variant={showCriticalPath ? "default" : "outline"}
                    onClick={() => setShowCriticalPath(!showCriticalPath)}
                    className={`gap-1 ${showCriticalPath ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                  >
                    <AlertTriangle className="h-4 w-4" />
                    {showCriticalPath ? 'Hide' : 'Show'} Critical Path ({criticalPathIds.length})
                  </Button>
                )}
                {/* Dependency Toggle */}
                <Button 
                  size="sm" 
                  variant={showDependencies ? "default" : "outline"}
                  onClick={() => setShowDependencies(!showDependencies)}
                  className="gap-1"
                >
                  <GitBranch className="h-4 w-4" />
                  {showDependencies ? 'Hide' : 'Show'} Dependencies
                </Button>
                {canEdit && (
                  <>
                    <Button size="sm" variant="outline" onClick={onAddTestPeriod}>
                      <Clock className="h-4 w-4 mr-1" />
                      Add Testing Period
                    </Button>
                    <Button size="sm" onClick={onAddMilestone}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Milestone
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Gantt Chart Timeline */}
            <div className={`border rounded-lg relative ${showDependencies ? 'overflow-visible' : 'overflow-hidden'}`}>
              {/* Month Headers */}
              <div className="flex bg-slate-100 dark:bg-slate-800 border-b">
                <div className="w-48 flex-shrink-0 p-2 border-r font-medium text-sm">
                  Phase / Milestone
                </div>
                <div className="flex-1 relative">
                  <div className="flex h-10">
                    {/* Generate month columns */}
                    {(() => {
                      const months = [];
                      if (timelineData.project_start && timelineData.mvp_deadline) {
                        const start = parseLocalDate(timelineData.project_start);
                        const end = parseLocalDate(timelineData.mvp_deadline);
                        let current = new Date(start.getFullYear(), start.getMonth(), 1);
                        while (current <= end) {
                          months.push(new Date(current));
                          current.setMonth(current.getMonth() + 1);
                        }
                      }
                      return months.map((month, idx) => (
                        <div 
                          key={idx} 
                          className="flex-1 border-r last:border-r-0 flex items-center justify-center text-xs font-medium text-muted-foreground"
                        >
                          {format(month, 'MMM yyyy')}
                        </div>
                      ));
                    })()}
                  </div>
                  {/* Today marker line */}
                  {timelineData.project_start && timelineData.mvp_deadline && (
                    <div 
                      className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-50"
                      style={{ left: `${getPosition(new Date().toISOString().split('T')[0])}%` }}
                    >
                      <div className="absolute -top-0 left-1/2 -translate-x-1/2 text-[9px] font-bold text-red-500 bg-white dark:bg-slate-900 px-1 rounded">
                        TODAY
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Testing Periods Rows */}
              {timelineData.testing_periods?.length > 0 && (
                <div className="border-b-2 border-dashed">
                  {timelineData.testing_periods.map((period) => {
                    const startPos = getPosition(period.start_date);
                    const endPos = getPosition(period.end_date);
                    const width = Math.max(endPos - startPos, 2);
                    
                    return (
                      <div key={period.id} className="flex hover:bg-slate-50 dark:hover:bg-slate-800/50 group">
                        <div className="w-48 flex-shrink-0 p-2 border-r flex items-center gap-2">
                          <span className="text-purple-500">🧪</span>
                          <span className="text-sm truncate">{period.name}</span>
                        </div>
                        <div className="flex-1 relative py-2 px-1">
                          <div 
                            className="absolute h-6 bg-purple-400 dark:bg-purple-600 rounded-full opacity-80 flex items-center px-2 text-white text-[10px] font-medium cursor-pointer hover:opacity-100 transition-opacity"
                            style={{ left: `${startPos}%`, width: `${width}%`, top: '50%', transform: 'translateY(-50%)' }}
                            title={`${period.name}: ${format(parseLocalDate(period.start_date), 'MMM d')} - ${format(parseLocalDate(period.end_date), 'MMM d')}`}
                          >
                            <span className="truncate">{period.name}</span>
                          </div>
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onDeleteTestPeriod(period.id)}
                              className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-red-500"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onEditTestPeriod(period)}
                              className="absolute right-8 top-1/2 -translate-y-1/2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-blue-500"
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Milestone Rows */}
              {timelineData.milestones?.map((milestone, idx) => {
                const hasStartDate = milestone.start_date;
                const startPos = hasStartDate ? getPosition(milestone.start_date) : getPosition(milestone.target_date) - 2;
                const endPos = getPosition(milestone.target_date);
                const width = Math.max(endPos - startPos, 3);
                const phaseConfig = PHASE_CONFIG[milestone.phase_type] || PHASE_CONFIG.development;
                const isHovered = hoveredMilestone === milestone.id;
                const isOnCriticalPath = showCriticalPath && criticalPathIds.includes(milestone.id);
                
                // Determine bar color based on status and critical path
                const getBarStyle = () => {
                  if (milestone.status === 'completed') return 'bg-green-500';
                  if (milestone.status === 'delayed') return 'bg-red-500';
                  if (isOnCriticalPath) return 'bg-orange-500 ring-2 ring-orange-300 ring-offset-1';
                  if (milestone.status === 'in_progress') return phaseConfig.color;
                  return phaseConfig.lightColor;
                };
                
                return (
                  <div 
                    key={milestone.id} 
                    className={`flex hover:bg-slate-50 dark:hover:bg-slate-800/50 group ${idx % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-slate-50/50 dark:bg-slate-800/30'} ${isOnCriticalPath ? 'bg-orange-50 dark:bg-orange-900/20' : ''}`}
                    onMouseEnter={() => setHoveredMilestone(milestone.id)}
                    onMouseLeave={() => setHoveredMilestone(null)}
                  >
                    <div className="w-48 flex-shrink-0 p-2 border-r flex items-center gap-2">
                      {isOnCriticalPath && <AlertTriangle className="h-4 w-4 text-orange-500 flex-shrink-0" />}
                      <span>{phaseConfig.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isOnCriticalPath ? 'text-orange-600 dark:text-orange-400' : ''}`}>{milestone.name}</p>
                        <p className="text-[10px] text-muted-foreground">{phaseConfig.label}</p>
                      </div>
                      {milestone.status === 'completed' && <Check className="h-4 w-4 text-green-500 flex-shrink-0" />}
                    </div>
                    <div className="flex-1 relative py-2 px-1">
                      {/* Milestone Bar */}
                      <div 
                        className={`absolute h-6 ${getBarStyle()} rounded-full flex items-center justify-between px-2 cursor-pointer transition-all duration-200 ${isHovered ? 'h-7 shadow-lg z-10' : ''}`}
                        style={{ left: `${startPos}%`, width: `${width}%`, top: '50%', transform: 'translateY(-50%)' }}
                        onClick={() => onSelectMilestone(milestone)}
                        data-testid={`milestone-bar-${milestone.id}`}
                      >
                        {/* Start dot */}
                        {hasStartDate && (
                          <div className="w-2 h-2 bg-white rounded-full flex-shrink-0" />
                        )}
                        {/* Label inside bar if wide enough */}
                        {width > 10 && (
                          <span className="text-white text-[10px] font-medium truncate mx-1 flex-1">
                            {milestone.name}
                          </span>
                        )}
                        {/* End dot */}
                        <div className="w-3 h-3 bg-white rounded-full flex-shrink-0 flex items-center justify-center">
                          {milestone.status === 'completed' && <Check className="h-2 w-2 text-green-600" />}
                        </div>
                      </div>
                      
                      {/* Hover tooltip */}
                      {isHovered && (
                        <div className="absolute left-1/2 -translate-x-1/2 -top-16 bg-slate-900 dark:bg-slate-700 text-white text-xs p-2 rounded shadow-lg z-50 whitespace-nowrap pointer-events-none">
                          <p className="font-medium">{milestone.name}</p>
                          <p className="text-slate-300 text-[10px]">Click to view details</p>
                          <p className="text-slate-300">
                            {milestone.start_date && `${format(parseLocalDate(milestone.start_date), 'MMM d')} → `}
                            {format(parseLocalDate(milestone.target_date), 'MMM d, yyyy')}
                          </p>
                          <p className={`${milestone.status === 'completed' ? 'text-green-400' : milestone.status === 'delayed' ? 'text-red-400' : 'text-blue-400'}`}>
                            {milestone.status.charAt(0).toUpperCase() + milestone.status.slice(1).replace('_', ' ')}
                          </p>
                        </div>
                      )}
                      
                      {/* Edit controls on hover */}
                      {canEdit && (
                        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEditMilestone(milestone)}
                            className="h-6 w-6 p-0 text-blue-500 bg-white dark:bg-slate-800"
                            title="Edit milestone"
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Select
                            value={milestone.status}
                            onValueChange={(value) => onUpdateMilestone(milestone.id, { status: value })}
                          >
                            <SelectTrigger className="h-6 w-24 text-[10px] bg-white dark:bg-slate-800">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="delayed">Delayed</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDeleteMilestone(milestone.id)}
                            className="h-6 w-6 p-0 text-red-500 bg-white dark:bg-slate-800"
                            title="Delete milestone"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Dependency Arrows SVG Overlay */}
              {showDependencies && timelineData.milestones?.some(m => m.dependencies?.length > 0) && (
                <svg 
                  className="absolute pointer-events-none overflow-visible" 
                  style={{ 
                    left: '192px',
                    top: '40px',
                    width: 'calc(100% - 192px)',
                    height: `${(timelineData.testing_periods?.length || 0) * 40 + timelineData.milestones.length * 40}px`,
                    zIndex: 50
                  }}
                >
                  <defs>
                    <marker id="dependency-arrow" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto">
                      <path d="M0,0 L0,10 L10,5 z" fill="#8b5cf6" />
                    </marker>
                    <marker id="critical-path-arrow" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto">
                      <path d="M0,0 L0,10 L10,5 z" fill="#f97316" />
                    </marker>
                  </defs>
                  {timelineData.milestones?.map(milestone => {
                    if (!milestone.dependencies?.length) return null;
                    return milestone.dependencies.map(depId => {
                      const depMilestone = timelineData.milestones.find(m => m.id === depId);
                      if (!depMilestone) return null;
                      
                      const sourceIdx = timelineData.milestones.findIndex(m => m.id === depId);
                      const targetIdx = timelineData.milestones.findIndex(m => m.id === milestone.id);
                      const testPeriodsOffset = (timelineData.testing_periods?.length || 0) * 40;
                      
                      const isCriticalPathDep = showCriticalPath && 
                        criticalPathIds.includes(depId) && 
                        criticalPathIds.includes(milestone.id) &&
                        criticalPathIds.indexOf(milestone.id) === criticalPathIds.indexOf(depId) + 1;
                      
                      const sourceY = testPeriodsOffset + sourceIdx * 40 + 20;
                      const targetY = testPeriodsOffset + targetIdx * 40 + 20;
                      
                      const sourceEndPos = getPosition(depMilestone.target_date);
                      const targetStartPos = milestone.start_date 
                        ? getPosition(milestone.start_date) 
                        : Math.max(getPosition(milestone.target_date) - 5, 0);
                      
                      const controlX1 = Math.min(sourceEndPos + 8, 100);
                      const controlX2 = Math.max(targetStartPos - 8, 0);
                      const pathD = sourceY === targetY 
                        ? `M ${sourceEndPos}% ${sourceY} L ${targetStartPos}% ${targetY}`
                        : `M ${sourceEndPos}% ${sourceY} C ${controlX1}% ${sourceY}, ${controlX2}% ${targetY}, ${targetStartPos}% ${targetY}`;
                      
                      const arrowColor = isCriticalPathDep ? '#f97316' : '#8b5cf6';
                      const arrowMarker = isCriticalPathDep ? 'url(#critical-path-arrow)' : 'url(#dependency-arrow)';
                      
                      return (
                        <g key={`dep-${depId}-${milestone.id}`}>
                          <path
                            d={pathD}
                            stroke={arrowColor}
                            strokeWidth={isCriticalPathDep ? '3.5' : '2.5'}
                            fill="none"
                            strokeDasharray={isCriticalPathDep ? '0' : '8,4'}
                            markerEnd={arrowMarker}
                          />
                          <circle cx={`${sourceEndPos}%`} cy={sourceY} r={isCriticalPathDep ? '6' : '5'} fill={arrowColor} />
                        </g>
                      );
                    });
                  })}
                </svg>
              )}

              {/* Empty state */}
              {timelineData.milestones?.length === 0 && timelineData.testing_periods?.length === 0 && (
                <div className="p-8 text-center">
                  <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">No milestones or testing periods defined</p>
                  {canEdit && (
                    <Button className="mt-4" onClick={onAddMilestone}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add First Milestone
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Phase Legend */}
            <div className="flex items-center justify-center gap-4 text-xs flex-wrap mt-4">
              {Object.entries(PHASE_CONFIG).map(([key, config]) => (
                <div key={key} className="flex items-center gap-1">
                  <div className={`w-3 h-3 rounded ${config.color}`} />
                  <span>{config.label}</span>
                </div>
              ))}
              <div className="flex items-center gap-1 border-l pl-4 ml-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span>Completed</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span>Delayed</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-6 h-3 rounded bg-purple-400" />
                <span>Testing Period</span>
              </div>
              <div className="flex items-center gap-1 border-l pl-4 ml-2">
                <svg width="24" height="12" className="flex-shrink-0">
                  <line x1="0" y1="6" x2="20" y2="6" stroke="#8b5cf6" strokeWidth="2" strokeDasharray="4,2" />
                  <polygon points="20,3 20,9 24,6" fill="#8b5cf6" />
                </svg>
                <span>Dependency</span>
              </div>
              {criticalPathIds.length > 1 && (
                <div className="flex items-center gap-1">
                  <svg width="24" height="12" className="flex-shrink-0">
                    <line x1="0" y1="6" x2="20" y2="6" stroke="#f97316" strokeWidth="3" />
                    <polygon points="20,3 20,9 24,6" fill="#f97316" />
                  </svg>
                  <span className="text-orange-600 font-medium">Critical Path</span>
                </div>
              )}
            </div>

            {/* Critical Path Progress Info */}
            {showCriticalPath && criticalPathIds.length > 1 && (
              <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-orange-500" />
                  <h4 className="font-semibold text-orange-700 dark:text-orange-400">Critical Path Analysis</h4>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs">Chain Length</p>
                    <p className="font-medium text-orange-600">{criticalPathIds.length} milestones</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Completed</p>
                    <p className="font-medium text-green-600">
                      {timelineData.milestones?.filter(m => criticalPathIds.includes(m.id) && m.status === 'completed').length || 0} / {criticalPathIds.length}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Start Date</p>
                    <p className="font-medium">
                      {(() => {
                        const firstMilestone = timelineData.milestones?.find(m => m.id === criticalPathIds[0]);
                        return firstMilestone?.start_date ? format(parseLocalDate(firstMilestone.start_date), 'MMM d, yyyy') : 'N/A';
                      })()}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">End Date</p>
                    <p className="font-medium">
                      {(() => {
                        const lastMilestone = timelineData.milestones?.find(m => m.id === criticalPathIds[criticalPathIds.length - 1]);
                        return lastMilestone?.target_date ? format(parseLocalDate(lastMilestone.target_date), 'MMM d, yyyy') : 'N/A';
                      })()}
                    </p>
                  </div>
                </div>
                <div className="mt-3">
                  <p className="text-xs text-muted-foreground mb-1">Path Sequence:</p>
                  <div className="flex items-center gap-1 flex-wrap text-xs">
                    {criticalPathIds.map((id, idx) => {
                      const m = timelineData.milestones?.find(ms => ms.id === id);
                      if (!m) return null;
                      return (
                        <React.Fragment key={id}>
                          <span className={`px-2 py-0.5 rounded ${m.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                            {m.name}
                          </span>
                          {idx < criticalPathIds.length - 1 && <ArrowRight className="h-3 w-3 text-orange-400" />}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Testing Periods Section */}
            {timelineData.testing_periods?.length > 0 && (
              <div className="mt-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-purple-500" />
                  Testing Periods
                </h3>
                <div className="space-y-2">
                  {timelineData.testing_periods.map(period => (
                    <div key={period.id} className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded bg-purple-400" />
                        <div>
                          <p className="font-medium text-sm">{period.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {format(parseLocalDate(period.start_date), 'MMM d')} - {format(parseLocalDate(period.end_date), 'MMM d, yyyy')}
                          </p>
                          {period.description && (
                            <p className="text-xs text-muted-foreground mt-1">{period.description}</p>
                          )}
                        </div>
                      </div>
                      {canEdit && (
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => onEditTestPeriod(period)} className="h-7 w-7 p-0 text-blue-500" title="Edit testing period">
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => onDeleteTestPeriod(period.id)} className="h-7 w-7 p-0 text-red-500" title="Delete testing period">
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Milestone List */}
            {timelineData.milestones?.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">All Milestones</h3>
                  {canEdit && (
                    <Button 
                      size="sm" 
                      variant={reorderMode ? "default" : "outline"}
                      onClick={() => setReorderMode(!reorderMode)}
                      className="gap-1"
                    >
                      <ArrowUpDown className="h-4 w-4" />
                      {reorderMode ? 'Done Reordering' : 'Reorder'}
                    </Button>
                  )}
                </div>
                
                {reorderMode ? (
                  /* Reorder Mode - Drag and Drop */
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={timelineData.milestones.map(m => m.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2 max-h-96 overflow-y-auto border rounded-lg p-2 bg-slate-100 dark:bg-slate-900">
                        <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                          <GripVertical className="h-3 w-3" />
                          Drag milestones to reorder them
                        </p>
                        {timelineData.milestones.map(milestone => (
                          <SortableMilestoneRow
                            key={milestone.id}
                            milestone={milestone}
                            canEdit={canEdit}
                            onEdit={onEditMilestone}
                            onDelete={onDeleteMilestone}
                            onUpdate={onUpdateMilestone}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  /* Normal Mode - View Only */
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {timelineData.milestones.map(milestone => {
                      const phaseConfig = PHASE_CONFIG[milestone.phase_type] || PHASE_CONFIG.development;
                      return (
                        <div 
                          key={milestone.id} 
                          className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                          onMouseEnter={() => setHoveredMilestone(milestone.id)}
                          onMouseLeave={() => setHoveredMilestone(null)}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-sm">{phaseConfig.icon}</span>
                            <div className={`w-3 h-3 rounded-full ${getMilestoneColor(milestone.status)}`} />
                            <div>
                              <p className="font-medium text-sm">{milestone.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {milestone.start_date && `${format(parseLocalDate(milestone.start_date), 'MMM d')} → `}
                                {format(parseLocalDate(milestone.target_date), 'MMM d, yyyy')}
                                {milestone.hub && ` • ${milestone.hub}`}
                                <span className="ml-2 text-[10px] opacity-70">({phaseConfig.label})</span>
                              </p>
                            </div>
                          </div>
                          {canEdit && (
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-1">
                                <Input
                                  type="date"
                                  defaultValue={milestone.start_date || ''}
                                  onBlur={(e) => {
                                    if (e.target.value !== (milestone.start_date || '')) {
                                      onUpdateMilestone(milestone.id, { start_date: e.target.value });
                                    }
                                  }}
                                  className="h-7 w-32 text-xs"
                                  placeholder="Start"
                                  title="Start Date"
                                />
                                <span className="text-xs text-muted-foreground">→</span>
                                <Input
                                  type="date"
                                  defaultValue={milestone.target_date}
                                  onBlur={(e) => {
                                    if (e.target.value !== milestone.target_date) {
                                      onUpdateMilestone(milestone.id, { target_date: e.target.value });
                                    }
                                  }}
                                  className="h-7 w-32 text-xs"
                                  title="Target Date"
                                />
                              </div>
                              <Select
                                value={milestone.phase_type || 'development'}
                                onValueChange={(value) => onUpdateMilestone(milestone.id, { phase_type: value })}
                              >
                                <SelectTrigger className="h-7 w-24 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="planning">Planning</SelectItem>
                                  <SelectItem value="design">Design</SelectItem>
                                  <SelectItem value="development">Development</SelectItem>
                                  <SelectItem value="internal_testing">Int. Test</SelectItem>
                                  <SelectItem value="staff_testing">Staff Test</SelectItem>
                                  <SelectItem value="deploy_ready">Deploy</SelectItem>
                                  <SelectItem value="full_testing">Full Test</SelectItem>
                                  <SelectItem value="mvp_ready">MVP Ready</SelectItem>
                                </SelectContent>
                              </Select>
                              <Select
                                value={milestone.status}
                                onValueChange={(value) => onUpdateMilestone(milestone.id, { status: value })}
                              >
                                <SelectTrigger className="h-7 w-24 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="in_progress">In Progress</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                  <SelectItem value="delayed">Delayed</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button variant="ghost" size="sm" onClick={() => onEditMilestone(milestone)} className="h-7 w-7 p-0 text-blue-500" title="Edit milestone">
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => onDeleteMilestone(milestone.id)} className="h-7 w-7 p-0 text-red-500" title="Delete milestone">
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {timelineData.milestones?.length === 0 && (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground">No milestones defined yet</p>
                {canEdit && (
                  <Button className="mt-4" onClick={onAddMilestone}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add First Milestone
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TimelineModal;
